import styled from "@emotion/styled";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
} from "@mui/material";
import { Form } from "@shared/components/Form";
import { SubmitButton } from "@avenue-8/sidekick-ui";
import { useCallback, useEffect, useRef, useState } from "react";
import ReactCrop, { Crop, PixelCrop } from "react-image-crop";
import { centerAspectCrop, drawCanvas } from "./image-crop-helpers";

const MAX_HEIGHT = 400;

const CropAreaContainer = styled.div`
  text-align: center;
  background-color: ${(p) => p.theme.colors.background2};
  max-height: ${MAX_HEIGHT}px;
  min-height: ${MAX_HEIGHT}px;
  border-radius: 0.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
`;

type Props = {
  title: string;
  file: File | null;
  open: boolean;
  isLoading: boolean;
  /** The aspect ratio of the crop, e.g. `1` for a square or `16 / 9` for landscape. */
  aspect?: number;
  onSave: (croppedImage: File) => void;
  onCancel: () => void;
};

export const ImageCropDialog = (props: Props) => {
  const { title, file, open, isLoading, aspect, onSave, onCancel } = props;
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [imgSrc, setImgSrc] = useState<string | undefined>(undefined);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (file === null) return;
    setCrop(undefined);
    const reader = new FileReader();
    const onLoadImage = () => setImgSrc(reader.result?.toString() || "");
    reader.addEventListener("load", onLoadImage);
    reader.readAsDataURL(file);

    return () => {
      reader.removeEventListener("load", onLoadImage);
      setImgSrc(undefined);
      setCrop(undefined);
      setCompletedCrop(undefined);
    };
  }, [file]);

  function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    const { width, height } = e.currentTarget;
    setCrop(
      centerAspectCrop({
        mediaWidth: width,
        mediaHeight: height,
        aspect: aspect ?? (width > height ? width / height : height / width),
      })
    );
  }

  const handleSave = useCallback(() => {
    if (!completedCrop || !imgRef.current || !file) {
      return;
    }

    const canvas = drawCanvas({
      image: imgRef.current,
      canvas: document.createElement("canvas"),
      x: completedCrop.x,
      y: completedCrop.y,
      width: completedCrop.width,
      height: completedCrop.height,
    });

    if (!canvas) return;

    canvas.toBlob((blob) => {
      if (blob) {
        const scaledFile = new File([blob], file.name, { type: file.type });
        onSave(scaledFile);
      }
    }, file.type);
  }, [completedCrop, onSave, file]);

  return (
    <Dialog fullWidth open={open} maxWidth={"sm"}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ paddingBottom: 1 }}>
        <Form aria-disabled={isLoading}>
          <CropAreaContainer>
            <ReactCrop
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={aspect}
              minHeight={256}
              minWidth={256}
            >
              <Fade in={!!imgSrc && !!crop}>
                <img
                  ref={imgRef}
                  alt="Image to be cropped"
                  src={imgSrc}
                  style={{
                    maxHeight: MAX_HEIGHT,
                  }}
                  onLoad={onImageLoad}
                />
              </Fade>
            </ReactCrop>
          </CropAreaContainer>
        </Form>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={onCancel}
          sx={{ minWidth: "160px" }}
          type="button"
          disabled={isLoading}
          variant="outlined"
        >
          Cancel
        </Button>
        <SubmitButton
          onClick={handleSave}
          sx={{ minWidth: "160px" }}
          type="button"
          isLoading={isLoading}
          loadingText="Saving..."
        >
          Save
        </SubmitButton>
      </DialogActions>
    </Dialog>
  );
};
